import _ from 'lodash';
import { ImageKind, ColorModel, Image, type InterpolationAlgorithm } from 'image-js';

import { DisplayableImageData } from 'app/interfaces/Image';
import { Transformation } from 'app/interfaces/Image/Transformation';

export type TransformableImage = Image;

// Operation is done in place
function fitWWWC(img: Image, voi: { windowWidth: number; windowCenter: number }): Image {
  const { windowCenter, windowWidth } = voi;
  if (!(windowCenter && windowWidth)) return img;
  const maxValue = Math.pow(2, img.bitDepth) - 1;
  const channels = img.channels;
  const components = img.components;
  const offsetFromCenter = maxValue / 2 - voi.windowCenter;
  const scale = maxValue / windowWidth;

  if (offsetFromCenter === 0 && scale === 1) return img;

  for (let i = 0; i < img.data.length; i += channels) {
    for (let c = 0; c < components; c++) {
      const value = Math.round((img.data[i + c] - windowCenter) * scale + maxValue / 2);

      img.data[i + c] = Math.max(0, Math.min(maxValue, value));
    }
  }

  return img;
}

export function convertDisplayableImageToTransformableImage(
  image: DisplayableImageData
): TransformableImage | undefined {
  if (!image?.getPixelData()) return undefined;

  const pixelData = image.getPixelData() as unknown as Uint16Array | Uint8Array;

  const kind = image.color ? 'RGBA' : 'GREY';
  const colorModel = kind !== 'GREY' ? 'RGB' : 'GREY';
  const bitDepth = colorModel === 'GREY' ? pixelData.BYTES_PER_ELEMENT * 8 : 8;

  return new Image({
    data: pixelData,
    width: image.width,
    height: image.height,
    kind: kind as ImageKind,
    bitDepth,
    colorModel: colorModel as ColorModel,
  });
}

export function transformImage(
  img: TransformableImage,
  transformations: Transformation
): Image | undefined {
  const { crop, voi, invert, rotation, hflip, vflip, resize } = transformations;

  if (crop) img = img.crop(crop);
  if (resize) {
    let { width: resizeWidth, height: resizeHeight } = resize;

    if (resizeWidth && resizeHeight) {
      // We rescale before doing other operation to make other operations faster since we are usually resizing to lower image size.
      const widthScalingFactor = resizeWidth / img.width;
      const heightScalingFactor = resizeHeight / img.height;

      // Since we use the preserve Ratio option, we only keep the dimension that would make the image fit the resize dimension will preserving aspect ratio.
      if (widthScalingFactor < heightScalingFactor) {
        resizeHeight = undefined;
      } else {
        resizeWidth = undefined;
      }
    }

    // We only apply resize if resize dimension is different from the original image dimension.
    if (resizeWidth && resizeWidth !== img.width) {
      img = img.resize({ width: resizeWidth, preserveAspectRatio: true });
    } else if (resizeHeight && resizeHeight !== img.height) {
      img = img.resize({ height: resizeHeight, preserveAspectRatio: true });
    }
  }

  // VOI act on non inverted pixel, WWWC fit should be done before inversion
  if (voi) img = fitWWWC(img, voi);
  if (invert) img = img.invert({ inPlace: true });

  if (hflip) img = img.flipX();
  if (vflip) img = img.flipY();
  if (rotation) {
    // To avoid white background when rotating, we invert the image before and after rotation, this way tation background ends up black.
    img = img.invert({ inPlace: true });
    img = img.rotate(rotation, { interpolation: 'bilinear' as InterpolationAlgorithm.BILINEAR });
    img = img.invert({ inPlace: true });
  }

  return img;
}

// This method mutate input image
export function insertCanvasMaskIntoImage(image: TransformableImage, canvas: HTMLCanvasElement) {
  let canvasImage = Image.fromCanvas(canvas);
  canvasImage = canvasImage.grey();
  const valueScale = Math.max(...image.getMax()) / Math.max(...canvasImage.getMax());
  const maxY = Math.min(image.height, canvasImage.height);
  const maxX = Math.min(image.width, canvasImage.width);

  // image-js does not provide a addImage function so we implement our own.
  for (let j = 0; j < maxY; j++) {
    for (let i = 0; i < maxX; i++) {
      const pixelValue = canvasImage.getPixelXY(i, j);
      // if alpha non null
      if (pixelValue[0] > 0) {
        image.setPixelXY(i, j, [valueScale * pixelValue[0]]);
      }
    }
  }
  return image;
}

export function generateBitOverlayFromCanvas(canvas: HTMLCanvasElement) {
  let canvasImage = Image.fromCanvas(canvas);
  canvasImage = canvasImage.grey();
  const { width, height } = canvasImage;

  const overlayData = new Uint8Array(Math.ceil((width * height) / 8));

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const pixelIndex = y * width + x;
      const pixelValue = canvasImage.getPixelXY(x, y)[0];
      // Check if pixel has any visible content
      if (pixelValue === 0) continue;
      // Calculate the byte index and bit position within that byte
      const byteIndex = Math.floor(pixelIndex / 8);
      const bitPosition = pixelIndex % 8; // MSB first
      // Set the bit in the overlay data
      overlayData[byteIndex] |= 1 << bitPosition;
    }
  }

  return overlayData;
}

export function toCanvasDisplayableImage(transformableImage: TransformableImage) {
  const rgbaData = transformableImage.getRGBAData({ clamped: true }) as Uint8ClampedArray;

  return {
    data: rgbaData,
    size: { width: transformableImage.width, height: transformableImage.height },
  };
}

export function toBlob(transformableImage: TransformableImage, type: string, quality?: number) {
  return transformableImage.toBlob(type, quality);
}

export function prepareImageForCanvasDisplay(
  image: DisplayableImageData,
  transformations: Transformation
) {
  const transformableImage = convertDisplayableImageToTransformableImage(image);
  if (!transformableImage) return undefined;

  const transformedImage = transformImage(transformableImage, transformations);

  return toCanvasDisplayableImage(transformedImage);
}
